import React, {useCallback, useEffect, useMemo} from 'react';
import {Box, Card, Collapse, Paper} from '@mui/material';
import {useTranslation} from 'react-i18next';
import useIndustry from '../../../../../../../hooks/use-industry';
import Form, {MuiRFF, Yup} from '../../../../../../../components/form';
import {DashboardFilterParameter, DashboardViewFilterSectionFilters, WidgetForcedFilterRequirement} from "../../types";
import {DateRangeUtils} from "../../../../../../../components/app-specific/date-range-controller";
import DashboardViewFilterSectionActions, {DashboardViewFilterSectionActionsRef} from "./actions";
import DashboardViewFilterSectionContent from "./content";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
    ForcedFilterErrorKey,
    getForcedFilterFieldKeys,
    validateForcedFilters,
} from "../../utils/forced-filters";

export const schema = Yup.object({
    excludeCustomers: Yup.array().of(Yup.mixed().nullable().optional()),
    industry: Yup.mixed().nullable().optional(),
    currency: Yup.string().nullable().optional(),
    excludePartNumbers: Yup.array().of(Yup.mixed()).nullable().optional(),
    partNumbers: Yup.array().of(Yup.mixed()).nullable().optional(),
    partNumberPrefix: Yup.string().nullable().optional(),
    customers: Yup.array().of(Yup.mixed()).nullable().optional(),
    categories: Yup.array().of(Yup.mixed()).nullable().optional(),
    territoryCodes: Yup.array().of(Yup.string()).nullable().optional(),
    dateRangeValue: Yup.mixed().nullable().optional(),
    partSupportReasons: Yup.array().of(Yup.mixed()).nullable().optional(),
    partSupportStatuses: Yup.array().of(Yup.mixed()).nullable().optional(),
    partSupportTypes: Yup.array().of(Yup.mixed()).nullable().optional(),
    partSupportVendorStatuses: Yup.array().of(Yup.mixed()).nullable().optional(),
    warehouses: Yup.array().of(Yup.mixed()).nullable().optional(),
    salesWarehouses: Yup.array().of(Yup.mixed()).nullable().optional(),
    operators: Yup.array().of(Yup.mixed().nullable().optional()).nullable().optional(),
    partSupportExcludeReasons: Yup.array().of(Yup.mixed()).nullable().optional(),
    hasCredited: Yup.mixed().nullable().optional().nullable(),
    mergeWithParentFilters: Yup.boolean().nullable().optional(),
    provinces: Yup.array().of(Yup.mixed()).nullable().optional(),
    cities: Yup.array().of(Yup.string()).nullable().optional(),
    accountManagers: Yup.array().of(Yup.mixed()).nullable().optional(),
    arTransactionTypes: Yup.array().of(Yup.mixed()).nullable().optional(),
    vendorNumbers: Yup.array().of(Yup.string()).nullable().optional(),
// No start/end date validation here anymore, it is handled inside the value object if static.
});

export interface Schema extends Yup.InferType<typeof schema> {
}

export type SchemaKeys = keyof Schema;

export const validate = MuiRFF.makeValidate(schema);
export const required: Record<SchemaKeys, boolean> = MuiRFF.makeRequired(schema);


/**
 * Props interface for the DashboardViewFilterSection component
 */
export interface FilterSectionProps {
    disabled?: boolean;
    filters: DashboardViewFilterSectionFilters;
    setFilters: (value: DashboardViewFilterSectionFilters) => void;
    parameters: Array<DashboardFilterParameter>;
    canSaveFilters?: boolean;
    onClear?: () => void | Promise<void>;
    allowApplyingWithoutChange?: boolean;
    allowMergingWithParentFilters?: boolean;
    dashboardId?: number;
    visible: boolean;
    /** When true, only active filters are shown (disabled); available filters and actions are hidden. */
    viewOnly?: boolean;
    /** Widget-level forced filter rules. Omit on the dashboard filter bar. */
    forcedFilterRequirements?: WidgetForcedFilterRequirement[];
    /** Fill the parent dialog: fields scroll, actions stay pinned at the bottom. */
    dialogLayout?: boolean;
    /** Rendered above the filter fields inside the scrolling pane (dialog layout). */
    leadingContent?: React.ReactNode;
}

const DashboardViewFilterSection: React.FC<FilterSectionProps> = (props) => {
    const {filters, setFilters, forcedFilterRequirements} = props;
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.edit-widget-filters'});
    const {fetchedIndustry, fetchIndustry, cachedIndustry, loadingIndustries} = useIndustry({noCategories: true});
    const [collapseIn, setCollapseIn] = React.useState(() => (forcedFilterRequirements?.length ?? 0) > 0);
    const actionsRef = React.useRef<DashboardViewFilterSectionActionsRef>(null);

    const forcedFilterFieldKeys = useMemo(() => getForcedFilterFieldKeys(forcedFilterRequirements), [forcedFilterRequirements]);

    /**
     * Maps a forced-filter validation error to translated helper text.
     */
    const getForcedFilterErrorMessage = useCallback((errorKey: ForcedFilterErrorKey, min?: number, max?: number): string => {
        switch (errorKey) {
            case 'required':
                return t(t => t.requirements.errors.required);
            case 'min':
                return t(t => t.requirements.errors.min, {min});
            case 'max':
                return t(t => t.requirements.errors.max, {max});
            case 'minMax':
            default:
                return t(t => t.requirements.errors.minMax, {min, max});
        }
    }, [t]);

    const hasDateParameter = useMemo(() => props.parameters.includes(DashboardFilterParameter.StartDate) || props.parameters.includes(DashboardFilterParameter.EndDate), [props.parameters]);

    const fetchedIndustryId = (fetchedIndustry as any)?.id;
    const cachedIndustryId = (cachedIndustry as any)?.id;

    /**
     * Fetch industry data when industry filter changes
     */
    useEffect(() => {
        if ((filters.industry && fetchedIndustryId !== filters.industry) || !fetchedIndustryId) {
            void fetchIndustry({industryId: filters.industry ?? cachedIndustryId}, false);
        }
    }, [fetchIndustry, fetchedIndustryId, filters.industry, cachedIndustryId]);

    const generatePartNumbersValuesFromFilters = useCallback((partNumbers: string[] | undefined): any[] | undefined => {
        if (!partNumbers)
            return undefined;
        return partNumbers
            .map(e => {
                try {
                    return JSON.parse(e);
                } catch {
                    // in case the value is a string and not a stringified object.
                    // noinspection SuspiciousTypeOfGuard
                    if (typeof e === 'string' && e?.trim().length > 0)
                        return {partNo: e};
                    return undefined;
                }
            })
            .filter(Boolean) as any[] | undefined;
    }, []);

    /**
     * Generates form schema values from the provided filters
     */
    const generateSchemaValuesFromFilters = useCallback((filters: DashboardViewFilterSectionFilters): Partial<Schema> => {
        let dateRangeValue = DateRangeUtils.getDateRangeValueFromLegacyFilters(filters);
        const dateRangeFromFlag = DateRangeUtils.toDateRangeFromDateRangeFlag((filters as any).dateRangeFlag as string | undefined);
        const shouldUseDateRangeFromFlag = !!dateRangeFromFlag && !dateRangeValue;

        if (shouldUseDateRangeFromFlag && dateRangeFromFlag) {
            dateRangeValue = dateRangeFromFlag;
        }

        const excludePartNumbers = generatePartNumbersValuesFromFilters(filters.excludePartNumbers);
        return ({
            mergeWithParentFilters: filters?.mergeWithParentFilters ?? Boolean(forcedFilterRequirements?.length),
            currency: filters.currency ?? undefined,
            excludeCustomers: filters.excludeCustomers ? filters.excludeCustomers.map(e => JSON.parse(e)) : undefined,
            customers: filters.customers ? filters.customers.map(e => JSON.parse(e)) : undefined,
            excludePartNumbers: excludePartNumbers,
            partNumbers: filters.partNumbers ? filters.partNumbers.map(e => JSON.parse(e)) : undefined,
            partNumberPrefix: filters.partNumberPrefix ?? undefined,
            categories: filters.categories ? filters.categories.map(e => JSON.parse(e)) : undefined,
            territoryCodes: filters.territoryCodes,
            partSupportReasons: filters.partSupportReasons ? filters.partSupportReasons.map(e => JSON.parse(e)) : undefined,
            partSupportExcludeReasons: filters.partSupportExcludeReasons ? filters.partSupportExcludeReasons.map(e => JSON.parse(e)) : undefined,
            partSupportStatuses: filters.partSupportStatuses ? filters.partSupportStatuses.map(e => JSON.parse(e)) : undefined,
            partSupportTypes: filters.partSupportTypes ? filters.partSupportTypes.map(e => JSON.parse(e)) : undefined,
            partSupportVendorStatuses: filters.partSupportVendorStatuses ? filters.partSupportVendorStatuses.map(e => JSON.parse(e)) : undefined,
            hasCredited: filters.hasCredited === true ? 'true' : filters.hasCredited === false ? 'false' : 'null',
            warehouses: filters.warehouses ? filters.warehouses.map(e => JSON.parse(e)) : undefined,
            salesWarehouses: filters.salesWarehouses ? filters.salesWarehouses.map(e => JSON.parse(e)) : undefined,
            operators: filters.operators ? filters.operators.map(e => JSON.parse(e)) : undefined,
            industry: (filters.industry ?? cachedIndustryId),
            dateRangeValue: dateRangeValue,
            provinces: filters.provinces ? filters.provinces.map(e => JSON.parse(e)) : undefined,
            cities: filters.cities ? filters.cities : undefined,
            accountManagers: filters.accountManagers ? filters.accountManagers.map(e => JSON.parse(e)) : undefined,
            arTransactionTypes: filters.arTransactionTypes ? filters.arTransactionTypes.map(e => JSON.parse(e)) : undefined,
            vendorNumbers: filters.vendorNumbers,
            // TODO: add more filters
        })
    }, [cachedIndustryId, forcedFilterRequirements?.length, generatePartNumbersValuesFromFilters]);

    /**
     * Generates filter values from the provided form schema values
     */
    const generateExcludedPartNumbersFilters = useCallback((excludePartNumbers: any[] | undefined): string[] | undefined => {
        if (!excludePartNumbers || excludePartNumbers.length === 0)
            return undefined;
        return excludePartNumbers
            .map(e => {
                try {
                    return JSON.stringify(e)
                } catch {
                    if (typeof e === 'string' && e?.trim().length > 0) {
                        // in case the value is a string and not a stringified object.
                        return JSON.stringify({partNo: e});
                    }
                    return undefined;
                }
            })
            .filter(Boolean) as string[] | undefined;
    }, []);

    /**
     * Generates filters from the provided form schema values
     */
    const generateFiltersFromSchemaValues = useCallback((values: Partial<Record<keyof DashboardViewFilterSectionFilters, any>>): DashboardViewFilterSectionFilters => {
        const excludedPartNumbers = generateExcludedPartNumbersFilters(values.excludePartNumbers);
        return {
            mergeWithParentFilters: values.mergeWithParentFilters ?? false,
            currency: values.currency ?? undefined,
            territoryCodes: values.territoryCodes?.length ? values.territoryCodes : undefined,
            excludePartNumbers: excludedPartNumbers,
            excludeCustomers: values.excludeCustomers?.length ? values.excludeCustomers.map(e => JSON.stringify(e)) : undefined,
            customers: values.customers?.length ? values.customers.map(e => JSON.stringify(e)) : undefined,
            partNumbers: values.partNumbers?.length ? values.partNumbers.map(e => JSON.stringify(e)) : undefined,
            partNumberPrefix: values.partNumberPrefix ?? undefined,
            categories: values.categories?.length ? values.categories.map(e => JSON.stringify(e)) : undefined,
            partSupportReasons: values.partSupportReasons?.length ? values.partSupportReasons.map(e => JSON.stringify(e)) : undefined,
            partSupportExcludeReasons: values.partSupportExcludeReasons?.length ? values.partSupportExcludeReasons.map(e => JSON.stringify(e)) : undefined,
            partSupportStatuses: values.partSupportStatuses?.length ? values.partSupportStatuses.map(e => JSON.stringify(e)) : undefined,
            partSupportTypes: values.partSupportTypes?.length ? values.partSupportTypes.map(e => JSON.stringify(e)) : undefined,
            partSupportVendorStatuses: values.partSupportVendorStatuses?.length ? values.partSupportVendorStatuses.map(e => JSON.stringify(e)) : undefined,
            hasCredited: (values.hasCredited as unknown as any) === 'null' || values.hasCredited === undefined ? undefined : (values.hasCredited as unknown as any) === 'true',
            warehouses: values.warehouses?.length ? values.warehouses.map(e => JSON.stringify(e)) : undefined,
            salesWarehouses: values.salesWarehouses?.length ? values.salesWarehouses.map(e => JSON.stringify(e)) : undefined,
            operators: values.operators?.length ? values.operators.map(e => JSON.stringify(e)) : undefined,
            industry: values.industry ?? undefined,
            dateRangeValue: values.dateRangeValue ? JSON.stringify(values.dateRangeValue) : undefined,
            provinces: values.provinces?.length ? values.provinces.map(e => JSON.stringify(e)) : undefined,
            cities: values.cities?.length ? values.cities : undefined,
            accountManagers: values.accountManagers?.length ? values.accountManagers.map(e => JSON.stringify(e)) : undefined,
            arTransactionTypes: values.arTransactionTypes?.length ? values.arTransactionTypes.map(e => JSON.stringify(e)) : undefined,
            vendorNumbers: values.vendorNumbers?.length ? values.vendorNumbers : undefined,
            // TODO: add more filters
        }
    }, [generateExcludedPartNumbersFilters]);

    const initialValues = useMemo<Partial<Record<keyof DashboardViewFilterSectionFilters, any>>>(() => generateSchemaValuesFromFilters(filters), [filters, generateSchemaValuesFromFilters]);

    /**
     * Default values for each filter parameter
     */
    const DEFAULT_VALUES = useMemo<Partial<Record<keyof DashboardViewFilterSectionFilters, any>>>(() => ({
        industry: cachedIndustryId,
        hasCredited: 'null',
    } as const), [cachedIndustryId]);

    /**
     * Checks if a value is different from its default value
     */
    const isDifferentFromDefault = useCallback((param: keyof DashboardViewFilterSectionFilters, values: Partial<Record<keyof DashboardViewFilterSectionFilters, any>>): boolean => {
        const defaultValue = DEFAULT_VALUES[param];

        const value = values[param];
        if (!defaultValue)
            return value !== undefined && value !== null && value !== '' && (!(Array.isArray(value) && value.length === 0));

        return value !== defaultValue;
    }, [DEFAULT_VALUES]);


    /**
     * Check if any parameter has a non-default value
     */
    const hasValues = useCallback((values: Schema): boolean =>
            Object.entries(values).some(([paramKey, value]) => {
                if (paramKey === 'mergeWithParentFilters')
                    return false;
                if (!value) return false;
                return isDifferentFromDefault(paramKey as any, values);
            }),
        [isDifferentFromDefault]);

    const activeValues = useMemo(() => Object.entries(initialValues)
            .filter(([paramKey, value]) => {
                if (paramKey === 'mergeWithParentFilters')
                    return false;
                if (loadingIndustries && paramKey === 'industry')
                    return false;
                if (!value) return false;
                return isDifferentFromDefault(paramKey as any, initialValues);
            })
            .filter(([paramKey]) => {
                if (hasDateParameter) {
                    if (paramKey === 'dateRangeValue') {
                        return true;
                    }
                }
                return true;
            })
            .filter(([, value]) => !!value) as [keyof Schema, any][],
        [hasDateParameter, isDifferentFromDefault, loadingIndustries, initialValues]);

    /**
     * Validates the filter form, including widget forced-filter requirements.
     */
    const validateForm = useCallback(async (values: Schema) => {
        const baseErrors = (await validate(values)) ?? {};
        if (!forcedFilterRequirements?.length) {
            return baseErrors;
        }
        const result = validateForcedFilters(values as Partial<Record<string, unknown>>, forcedFilterRequirements);
        for (const item of result.results) {
            if (!item.met && item.fieldKey && item.errorKey) {
                (baseErrors as Record<string, string>)[item.fieldKey] = getForcedFilterErrorMessage(
                    item.errorKey,
                    item.requiredMinCount,
                    item.requiredMaxCount,
                );
            }
        }
        return baseErrors;
    }, [forcedFilterRequirements, getForcedFilterErrorMessage]);

    /**
     * Renders the filter fields used by both the page bar and the dialog layout.
     */
    const formFields = useCallback((formProps: any) => (
        <DashboardViewFilterSectionContent
            {...formProps}
            {...props}
            collapseIn={collapseIn}
            setCollapseIn={setCollapseIn}
            hasValues={hasValues}
            activeValues={activeValues}
            forcedFilterFieldKeys={forcedFilterFieldKeys}
        />
    ), [activeValues, collapseIn, forcedFilterFieldKeys, hasValues, props]);

    /**
     * Handle form submission
     * Converts UTC dates back to server format before updating filters
     */
    const onSubmit = useCallback((values: Schema) => {
        const filters = generateFiltersFromSchemaValues(values);
        actionsRef.current?.onFormSubmitted(values, filters);
        setFilters(filters);
        setCollapseIn(false);
    }, [generateFiltersFromSchemaValues, setFilters]);

    if (props.dialogLayout) {
        return (
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    width: '100%',
                }}
            >
                <Form<Schema, Partial<Schema>>
                    onSubmit={onSubmit}
                    validate={validateForm}
                    initialValues={initialValues}
                    formProps={{
                        style: {
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        },
                    }}
                >
                    {(formProps) => (
                        <>
                            <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 2}}>
                                {props.leadingContent}
                                {formFields(formProps)}
                            </Box>
                            {
                                !props.viewOnly &&
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        backgroundColor: 'background.paper',
                                        px: 3,
                                        py: 2,
                                    }}
                                >
                                    <DashboardViewFilterSectionActions
                                        ref={actionsRef}
                                        {...formProps}
                                        {...props}
                                        hasValues={hasValues}
                                        activeValues={activeValues}
                                        generateSchemaValuesFromFilters={generateSchemaValuesFromFilters}
                                        generateFilterValuesFromSchema={generateFiltersFromSchemaValues}
                                    />
                                </Box>
                            }
                        </>
                    )}
                </Form>
            </Box>
        );
    }

    return (
        <Card
            variant={'outlined'}
            sx={{
                marginBlock: 2,
                padding: 2,
                borderColor: 'divider',
                backgroundColor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                transition: theme => [
                    theme.transitions.create(['padding', 'margin'], {
                        duration: theme.transitions.duration.short,
                        delay: props.visible ? theme.transitions.duration.short : (theme.transitions.duration.enteringScreen / 2),
                    }),
                    theme.transitions.create('opacity', {
                        duration: theme.transitions.duration.short,
                        delay: props.visible ? theme.transitions.duration.short : (theme.transitions.duration.enteringScreen / 2),
                    }),
                ].join(', '),
                ...(!props.visible && {
                    marginBlock: 0,
                    padding: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                })
            }}
        >
            <Collapse in={props.visible}>
                <Paper sx={{boxShadow: 'none', padding: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Form<Schema, Partial<Schema>>
                        onSubmit={onSubmit}
                        validate={validateForm}
                        initialValues={initialValues}
                    >
                        {(formProps) => (
                            <>
                                <Grid2 container spacing={4}>
                                    <Grid2 xs={12}>
                                        <DashboardViewFilterSectionContent
                                            {...formProps}
                                            {...props}
                                            collapseIn={collapseIn}
                                            setCollapseIn={setCollapseIn}
                                            hasValues={hasValues}
                                            activeValues={activeValues}
                                            forcedFilterFieldKeys={forcedFilterFieldKeys}
                                        />
                                    </Grid2>
                                    {
                                        !props.viewOnly &&
                                        <Grid2 xs={12}>
                                            <DashboardViewFilterSectionActions
                                                ref={actionsRef}
                                                {...formProps}
                                                {...props}
                                                hasValues={hasValues}
                                                activeValues={activeValues}
                                                generateSchemaValuesFromFilters={generateSchemaValuesFromFilters}
                                                generateFilterValuesFromSchema={generateFiltersFromSchemaValues}
                                            />
                                        </Grid2>
                                    }
                                </Grid2>
                            </>
                        )}
                    </Form>
                </Paper>
            </Collapse>
        </Card>
    );
};


export default DashboardViewFilterSection;